import React, { useEffect, useMemo, useRef } from "react";

import {
  Column,
  Table,
  useReactTable,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel,
  FilterFn,
  flexRender,
} from "@tanstack/react-table";
import classNames from "classnames";
import { Label } from "reactstrap";

declare module "@tanstack/table-core" {
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
    filterDate: FilterFn<unknown>;
    equals: FilterFn<unknown>;
  }
}

function AppTable({
  data,
  columns,
  defaultPageSize,
  fetchData,
  getTrProps = (props) => props,
  hideDataUntilFilter = false,
  minFilterLength = 4,
  initialFilters,
  onFiltersChange,
}: {
  data: any[];
  columns: any[];
  defaultPageSize: number;
  fetchData: ({
    pageSize,
    pageIndex,
  }: {
    pageSize: number;
    pageIndex: number;
  }) => void;
  getTrProps: (props: any) => any;
    hideDataUntilFilter?: boolean;
    minFilterLength?: number;
    initialFilters?: { columnFilters: ColumnFiltersState; globalFilter: string };
    onFiltersChange?: (columnFilters: ColumnFiltersState, globalFilter: string) => void;
}) {
  
  function reducer(state: any, action: any) {
    fetchData({ pageSize: 0, pageIndex: 0 });
  }

  const [, rerender] = React.useReducer<any, any>(reducer, {});

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(initialFilters?.columnFilters ?? []);
  const [globalFilter, setGlobalFilter] = React.useState(initialFilters?.globalFilter ?? "");
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (onFiltersChange) {
      onFiltersChange(columnFilters, globalFilter);
    }
  }, [columnFilters, globalFilter, onFiltersChange]);

  const hasFilter = useMemo(() => {
    const allFilters = [
      ...(globalFilter ? [{ id: "global", value: globalFilter }] : []),
      ...columnFilters,
    ];
    return allFilters.some(
      (f) => typeof f.value === "string" && f.value.trim().length >= minFilterLength
    );
  }, [globalFilter, columnFilters, minFilterLength]);


  const protectedData = useMemo(() => {
    return (!hideDataUntilFilter || hasFilter ? data : [])
  }, [data, hideDataUntilFilter, hasFilter]);


  const table = useReactTable({
    data: protectedData,
    columns,
    state: {
      columnFilters,
      globalFilter,
    },
    filterFns: {
      fuzzy: (value, filterValue) => {
        return String(value).toLowerCase().includes(filterValue.toLowerCase());
      },
      equals: (row, columnId, filterValue) => {
        return String(row.getValue(columnId)).toLowerCase() === String(filterValue).toLowerCase();
      },
      filterDate: (row, columnId, value) => {
        const date = row.getValue(columnId) as Date;
        const [start, end] = value;
        const endIncluding = end ? end.getTime() + 86400000 : null

        if ((start || end) && !date) return false;
        if (start && !end) {
          return date.getTime() >= start.getTime();
        } else if (!start && end) {
          return date.getTime() <= endIncluding;
        } else if (start && end) {
          return date.getTime() >= start.getTime() && date.getTime() <= endIncluding;
        } else return true;
      }
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    debugTable: true,
    debugHeaders: true,
    debugColumns: false,
  });

  useEffect(() => {
    rerender(table.getState(), "");
    table.setPageSize(defaultPageSize);
  }, []);

  React.useEffect(() => {
    if (table.getState().columnFilters[0]?.id === "fullName") {
      if (table.getState().sorting[0]?.id !== "fullName") {
        table.setSorting([{ id: "fullName", desc: false }]);
      }
    }
  }, [table.getState().columnFilters[0]?.id]);

  return (
    <div className="p-2">
      {(hideDataUntilFilter) &&
        <div style={{
          marginBottom: 20, marginLeft: 10, display: "inline-flex", alignItems: "baseline", gap: "6px", color: "gray", alignSelf: "right"
        }}>
          <i className="las la-info-circle" style={{ fontSize: 25 }}></i>
          <Label style={{ fontSize: 16 }}>Enter text into any search bar to see results</Label>
        </div>

      }

      <div>
        <DebouncedInput
          value={globalFilter ?? ""}
          onChange={(value) => setGlobalFilter(String(value))}
          className="form-control"
          placeholder="Search all columns..."
        />
      </div>
      <div className="h-2" />
      <div className="ReactTable -striped -highlight">
        <table className="rt-table">
          <thead className="rt-thead -header">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="rt-tr">
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={classNames("rt-th rt-resizable-header")}>
                      {header.isPlaceholder ? null : (
                        <>
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? "cursor-pointer select-none"
                                : "",
                              onClick: header.column.getToggleSortingHandler(),
                            }}>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {header.column.getCanSort() && (
                              <>
                                {{
                                  asc: " 🔼",
                                  desc: " 🔽",
                                }[header.column.getIsSorted() as string] ?? <span className="sort-indicator">🔼🔽</span>}
                              </>
                            )}
                          </div>
                          {header.column.getCanFilter() ? (
                            <div>
                              <Filter column={header.column} table={table} />
                            </div>
                          ) : null}
                        </>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="rt-tbody">
            {table.getRowModel().rows.map((row, i) => {
              return (
                <tr
                  key={row.id}
                  className={classNames(
                    "rt-tr",
                    { " -odd": i % 2 === 0 },
                    { " -even": i % 2 === 1 }
                  )}
                  {...getTrProps(row.original)}>
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td key={cell.id} className="rt-td">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="h-2" />
      <div className="pagination-className">
        <button
          className="border rounded p-1"
          onClick={() => table.setPageIndex(0)}
          disabled={!table.getCanPreviousPage()}>
          {"<<"}
        </button>
        <button
          className="border rounded p-1"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}>
          {"<"}
        </button>
        <button
          className="border rounded p-1"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}>
          {">"}
        </button>
        <button
          className="border rounded p-1"
          onClick={() => table.setPageIndex(table.getPageCount() - 1)}
          disabled={!table.getCanNextPage()}>
          {">>"}
        </button>
        <span className="flex items-center gap-1">
          <div>Page</div>
          <strong>
            {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </strong>
        </span>
        <span className="flex items-center gap-1">
          | Go to page:
          <input
            type="number"
            defaultValue={table.getState().pagination.pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0;
              table.setPageIndex(page);
            }}
            className="border p-1 rounded w-16"
          />
        </span>
        <select
          value={table.getState().pagination.pageSize}
          onChange={(e) => {
            table.setPageSize(Number(e.target.value));
          }}>
          {[20, 50, 100].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              Show {pageSize}
            </option>
          ))}
        </select>
      </div>
      <div>{table.getPrePaginationRowModel().rows.length} Rows</div>
      {/* <div>
        <button onClick={() => rerender()}>Force Rerender</button>
      </div>
      <pre>{JSON.stringify(table.getState(), null, 2)}</pre> */}
    </div>
  );
}

function isValidDate(date: any) {
  const parsedDate = new Date(date);
  return parsedDate instanceof Date && !Number.isNaN(parsedDate);
}

function formatDate(date: Date) {
  try {
    const formattedDate = date.toISOString().split('T')[0]
    return formattedDate
  } catch (error) {
    return ''
  }
}

function Filter({
  column,
  table,
}: {
  column: Column<any, unknown>;
  table: Table<any>;
}) {
  const firstValue = table
    .getPreFilteredRowModel()
    .flatRows[0]?.getValue(column.id);

  const columnFilterValue = column.getFilterValue();

  const sortedUniqueValues = React.useMemo(
    () =>
      typeof firstValue === "number"
        ? []
        : Array.from(column.getFacetedUniqueValues().keys()).sort(),
    [column.getFacetedUniqueValues()]
  );

  if (firstValue instanceof Date) {
    const values = columnFilterValue as [Date, Date];
    const startDate = values?.[0];
    const endDate = values?.[1];
    return (
      <div>
        <div className="flex space-x-2 mb-3">
          <DebouncedInput
            type="date"
            value={startDate ? formatDate(new Date(startDate)) : ''}
            onChange={(value) => {
              if (isValidDate(value)) {
                column.setFilterValue((old: [Date, Date]) => [
                  value == '' ? null : new Date(value + 'T00:00:00'),
                  old?.[1],
                ]);
              }
            }}
            className="w-24 border rounded"
          />
          <DebouncedInput
            type="date"
            value={endDate ? formatDate(new Date(endDate)) : ''}
            onChange={(value) => {
              if (isValidDate(value)) {
                column.setFilterValue((old: [Date, Date]) => [
                  old?.[0],
                  value == '' ? null : new Date(value + 'T00:00:00'),
                ]);
              }
            }}
            className="w-24 border rounded "
          />
        </div>
        <div className="h-1" />
      </div>
    );
  }




  return typeof firstValue === "number" ? (
    <div>
      <div className="flex space-x-2 mb-3">
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[0] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [number, number]) => [value, old?.[1]])
          }
          placeholder={`Min ${
            column.getFacetedMinMaxValues()?.[0]
              ? `(${column.getFacetedMinMaxValues()?.[0]})`
              : ""
          }`}
          className="w-24 border shadow rounded"
        />
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[1] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [number, number]) => [old?.[0], value])
          }
          placeholder={`Max ${
            column.getFacetedMinMaxValues()?.[1]
              ? `(${column.getFacetedMinMaxValues()?.[1]})`
              : ""
          }`}
          className="w-24 border shadow rounded"
        />
      </div>
      <div className="h-1" />
    </div>
  ) : (
    <div className="mb-3">
      <datalist id={column.id + "list"}>
        {sortedUniqueValues.map((value: any) => (
          <option value={value} key={value} />
        ))}
      </datalist>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? "") as string}
        onChange={(value) => column.setFilterValue(value)}
        placeholder={`Search... (${column.getFacetedUniqueValues().size})`}
        className="form-control"
        list={column.id + "list"}
      />
      <div className="h-1" />
    </div>
  );
}

// A debounced input react component
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) {
  const [value, setValue] = React.useState(initialValue);

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value]);

  return (
    <input
      {...props}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
}

export default AppTable;
